import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { SILVER_CARD_PRICING, GOLD_CARD_PRICE } from '../lib/calculator';

const EXCLUDED = new Set(['/warenkorb']);

/**
 * Mobile Sticky-Kaufleiste (nur < md).
 * Zeigt Kartenbild + Live-Preis aus dem globalen Warenkorb-State (CartContext) —
 * dadurch überall dieselbe Summe wie im Konfigurator, Drawer und an der Kasse.
 */
export default function StickyMobileCTA() {
  const { pathname } = useLocation();
  const { openCart, isOpen, config, hasItems, touched } = useCart();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 350);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  if (EXCLUDED.has(pathname) || isOpen || !visible) return null;

  // Modus aus dem Warenkorb-State — Sticky-Leiste zeigt jedoch immer den günstigsten Preis
  const live = touched || hasItems;
  const isPvt = (config?.mode || 'private') === 'private';
  const cheapest = isPvt ? SILVER_CARD_PRICING[0].price : GOLD_CARD_PRICE;

  const img = isPvt ? '/img/silver_card_front_sm.webp' : '/img/gold_card_front_sm.webp';
  const eyebrow = live ? (isPvt ? 'Silver Card' : 'Gold Card') : 'FuelX Card';
  // Sticky-Leiste zeigt IMMER zuerst den günstigsten Einstiegspreis (Conversion-Anker)
  const priceLabel = `ab ${cheapest.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €/${isPvt ? 'Jahr' : 'Fzg/Jahr'}`;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-stone-200 shadow-[0_-8px_28px_rgba(0,0,0,0.10)] px-4 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] flex items-center gap-2.5 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="sticky-mobile-cta"
    >
      {/* Card-Thumbnail — auf sehr schmalen Geräten (<350px) ausgeblendet, damit Text lesbar bleibt */}
      <div className="w-11 h-7 flex-shrink-0 rounded-md overflow-hidden bg-stone-950 hidden min-[350px]:flex items-center justify-center">
        <img
          src={img}
          alt="FuelX Card"
          width="160"
          height="160"
          className="w-full h-full object-contain"
          loading="lazy"
          decoding="async"
          data-testid="sticky-mobile-cta-card-img"
        />
      </div>
      {/* Label + Preis */}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.16em] text-stone-500 font-semibold leading-none truncate">{eyebrow}</p>
        <p className="text-[13px] font-semibold text-stone-900 truncate leading-tight mt-0.5" data-testid="sticky-mobile-cta-price">{priceLabel}</p>
        <p className="text-[10px] text-emerald-700 font-medium leading-snug">Sorgenfrei Auto fahren mit FuelX!</p>
      </div>
      <button
        type="button"
        onClick={() => openCart()}
        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-2.5 rounded-full bg-emerald-600 text-white text-[13px] font-semibold shadow-lg shadow-emerald-600/25 active:scale-[0.97] transition-transform"
        data-testid="sticky-mobile-cta-btn"
      >
        Card sichern <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.2} />
      </button>
    </div>
  );
}
